import React from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { Area, AreaChart, Bar, BarChart, Cell, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { KpiTile, Section, fmtRate, formatAed, formatCount } from './common';

const MOVEMENTS = [
  { key: 'new', label: 'New', sign: 1, color: '#73BF69' },
  { key: 'expansion', label: 'Upgrades', sign: 1, color: '#5794F2' },
  { key: 'reactivation', label: 'Won back', sign: 1, color: '#8AB8FF' },
  { key: 'contraction', label: 'Downgrades', sign: -1, color: '#FF9830' },
  { key: 'churn', label: 'Lost', sign: -1, color: '#F2495C' },
];

/** Thaklis Pro's own revenue and growth (design §3.2). */
const RevenuePanel = ({ revenue, source }) => {
  const theme = useTheme();
  const delta = revenue.mrrPrev != null ? revenue.mrr - revenue.mrrPrev : null;
  const movementData = MOVEMENTS.map((m) => ({ ...m, value: m.sign * (revenue.movements?.[m.key] || 0) }));
  const nextRenewal = revenue.renewalsDue30?.[0];

  return (
    <Section title="Thaklis Pro revenue" source={source}>
      <Grid container spacing={1.5}>
        <Grid item xs={6} md={2}>
          <KpiTile
            label="Monthly recurring revenue"
            value={formatAed(revenue.mrr)}
            sub={delta == null ? null : `${delta >= 0 ? '+' : '−'}${formatAed(Math.abs(delta))} vs last period`}
            tooltip="Monthly value of active paid subscriptions (annual ÷ 12, after discounts)"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiTile
            label="Paying / free centers"
            value={`${formatCount(revenue.paying)} / ${formatCount(revenue.free)}`}
            sub={`ARPA ${formatAed(revenue.arpa)} · ${revenue.freeGrant || 0} free grant`}
            tooltip="Average revenue per paying center"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiTile
            testId="kpi-centers-lost"
            label="Centers lost"
            value={fmtRate(revenue.churned, revenue.payingAtStart)}
            sub="paying centers that did not renew"
            tooltip="Small-business SaaS: under 2% a month is excellent, 3–7% is common"
            color={revenue.churned ? '#F2495C' : undefined}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiTile
            label="Free → paid"
            value={fmtRate(revenue.converted, revenue.freeAtStart)}
            sub="free centers that upgraded"
            tooltip="Freemium benchmark: 3–5% good"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiTile
            label="Renewals due (30 days)"
            value={formatCount(revenue.renewalsDue30?.length ?? null)}
            sub={nextRenewal ? `next: ${nextRenewal.name} · ${nextRenewal.periodEnd}` : 'none due'}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiTile
            label="Failed renewals"
            value={formatCount(revenue.failedRenewals)}
            sub={`${revenue.recovered || 0} recovered · discounts ${formatAed(revenue.discounts)}`}
            color={revenue.failedRenewals ? '#F2495C' : undefined}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>Where MRR moved this period</Typography>
          <Box sx={{ height: 150 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={movementData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={80} />
                <ChartTooltip formatter={(v) => formatAed(v)} />
                <Bar dataKey="value" barSize={14}>
                  {movementData.map((m) => <Cell key={m.key} fill={m.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>MRR, last 12 months</Typography>
          <Box sx={{ height: 150 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue.months || []} margin={{ left: 0, right: 10 }}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={50} />
                <ChartTooltip formatter={(v) => formatAed(v)} />
                <Area type="monotone" dataKey="mrr" stroke={theme.palette.secondary.main} fill={theme.palette.secondary.main} fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
};

export default RevenuePanel;
